import { effectScope } from 'vue';
import useSettingsGlobals from '@/composables/useSettingsGlobals';
import useUserGlobals from '@/composables/useUserGlobals';
import useStorageSystem from '@/composables/useStorageSystem';

const STORE_NAME = 'userTipsStore';

// Shape matches the legacy Core-Web UserTips store so existing dismissals carry over.
let store;

// useStorageSystem needs an active Pinia, so the shared store is created on
// first use (inside component setup) rather than at module scope. The detached
// scope keeps the persistence watch alive after the initializing component
// unmounts.
function ensureStore() {
  if (store) return;

  effectScope(true).run(() => {
    [store] = useStorageSystem({}, STORE_NAME);
  });
}

function postUserTipAction(id, action, platform) {
  try {
    if (platform === 'android') {
      alert(`userTipAction,${action},${id}`);
    } else {
      window.webkit.messageHandlers.observe.postMessage({
        type: 'userTipAction',
        action,
        id,
      });
    }
  } catch {
    console.warn('messageHandlers not available.');
  }
}

export default function useUserTips() {
  // Read the globals here rather than at module scope: this module is imported
  // during the app's import graph, which can run before window.$App exists.
  const { $WEB_VIEW, $PLATFORM } = useSettingsGlobals();
  const { $USER_TIPS } = useUserGlobals();

  ensureStore();

  const isDismissed = (id) => {
    const tipId = String(id);

    if (store.value[tipId]?.dismissed) return true;

    if ($WEB_VIEW) {
      // Native passes the tips to show via the user_tips URL parameter;
      // anything not listed is treated as dismissed.
      if (!$USER_TIPS) return true;

      return !$USER_TIPS.split(',').includes(tipId);
    }

    return false;
  };

  const dismiss = (id, action = 'dismiss') => {
    const tipId = String(id);

    // The useStorageSystem watch persists this (browser only; webviews skip
    // localStorage and report to native instead).
    store.value[tipId] = { ...store.value[tipId], dismissed: true };

    if ($WEB_VIEW) {
      postUserTipAction(tipId, action, $PLATFORM);
    }
  };

  return {
    isDismissed,
    dismiss,
  };
}
